import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import DevTable from "../components/DevTable";
import GroupedDevTable from "../components/GroupedDevTable";
import KanbanBoard from "../components/KanbanBoard";
import DevDialog from "../components/DevDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PRIORITIES, STATUSES, PRIORITY_ORDER, STATUS_ORDER } from "../lib/constants";
import { Plus, Table2, KanbanSquare, Search, Layers, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Developments() {
  const { canWrite, isPM } = useAuth();
  const [devs, setDevs] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({ sistemas: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [fPriority, setFPriority] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fSistema, setFSistema] = useState("all");
  const [groupBy, setGroupBy] = useState("none");
  const [toDelete, setToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [showProd, setShowProd] = useState(false);
  const PROD_STATUSES = ["en_produccion", "produccion_pendiente_firma"];

  const load = useCallback(() => {
    api.get("/developments").then((r) => setDevs(r.data));
  }, []);

  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/catalogs").then((r) => setCatalog(r.data));
  }, [load]);

  const sistemaOptions = Array.from(new Set([
    ...(catalog.sistemas || []).map((s) => s.name),
    ...devs.map((d) => d.sistema).filter(Boolean),
  ]));

  const departamentoOptions = Array.from(new Set([
    ...(catalog.departamentos || []),
    ...devs.map((d) => d.departamento_solicitante).filter(Boolean),
  ])).sort();
  const solicitanteOptions = Array.from(new Set([
    ...(catalog.solicitantes || []),
    ...devs.map((d) => d.solicitante).filter(Boolean),
  ])).sort();

  const filtered = devs.filter((d) => {
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !d.dev_id.toLowerCase().includes(search.toLowerCase())) return false;
    if (fPriority !== "all" && d.priority !== fPriority) return false;
    if (fStatus !== "all" && d.status !== fStatus) return false;
    if (fSistema !== "all" && d.sistema !== fSistema) return false;
    return true;
  });

  const tableDevs = showProd ? filtered : filtered.filter((d) => !PROD_STATUSES.includes(d.status));
  const hiddenProdCount = filtered.filter((d) => PROD_STATUSES.includes(d.status)).length;

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (d) => { setEditing(d); setDialogOpen(true); };

  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (rows) => setSelectedIds((prev) => {
    const n = new Set(prev);
    const allSel = rows.length > 0 && rows.every((d) => n.has(d.id));
    rows.forEach((d) => allSel ? n.delete(d.id) : n.add(d.id));
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const applyBulkStatus = async () => {
    if (!bulkStatus) { toast.error("Selecciona un estatus"); return; }
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setApplyingBulk(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => api.put(`/developments/${id}`, { status: bulkStatus })));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok) toast.success(`${ok} desarrollo(s) → ${STATUSES[bulkStatus].label}`);
      if (fail) toast.error(`${fail} no se pudieron cambiar (revisa checklist/permisos)`);
      clearSelection();
      setBulkStatus("");
      load();
    } finally {
      setApplyingBulk(false);
    }
  };

  const handleMove = async (id, newStatus) => {
    const dev = devs.find((d) => d.id === id);
    // optimistic
    setDevs((prev) => prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d)));
    try {
      await api.put(`/developments/${id}`, { status: newStatus });
      toast.success(`${dev.dev_id} → ${STATUSES[newStatus].label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/developments/${toDelete.id}`);
      toast.success("Desarrollo eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleNotify = async (dev) => {
    toast.loading("Enviando correo...", { id: "notify" });
    try {
      const { data } = await api.post(`/developments/${dev.id}/notify`);
      toast.success(`Correo enviado a ${data.recipients.length} destinatario(s)`, { id: "notify" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "notify" });
    }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Gestión</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5">Desarrollos</h1>
        </div>
        {canWrite && (
          <Button data-testid="new-dev-btn" onClick={openNew} className="rounded-sm bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-1" /> Nuevo desarrollo
          </Button>
        )}
      </div>

      <div className="p-8">
        <Tabs defaultValue="table">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <TabsList className="rounded-sm bg-slate-100">
              <TabsTrigger value="table" data-testid="tab-table" className="rounded-sm data-[state=active]:bg-white"><Table2 className="h-4 w-4 mr-1.5" />Tabla</TabsTrigger>
              <TabsTrigger value="kanban" data-testid="tab-kanban" className="rounded-sm data-[state=active]:bg-white"><KanbanSquare className="h-4 w-4 mr-1.5" />Kanban</TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input data-testid="search-input" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 rounded-sm w-48 h-9" />
              </div>
              <Select value={fPriority} onValueChange={setFPriority}>
                <SelectTrigger data-testid="filter-priority" className="rounded-sm w-36 h-9"><SelectValue placeholder="Prioridad" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda prioridad</SelectItem>
                  {PRIORITY_ORDER.map((p) => <SelectItem key={p} value={p}>{PRIORITIES[p].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="filter-status" className="rounded-sm w-36 h-9"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo estado</SelectItem>
                  {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUSES[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fSistema} onValueChange={setFSistema}>
                <SelectTrigger data-testid="filter-sistema" className="rounded-sm w-40 h-9"><SelectValue placeholder="Sistema" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo sistema</SelectItem>
                  {sistemaOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger data-testid="group-by" className="rounded-sm w-44 h-9">
                  <Layers className="h-4 w-4 mr-1.5 text-slate-400" /><SelectValue placeholder="Agrupar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin agrupar</SelectItem>
                  <SelectItem value="status">Agrupar por estado</SelectItem>
                  <SelectItem value="responsable">Agrupar por responsable</SelectItem>
                  <SelectItem value="solicitante">Agrupar por solicitante</SelectItem>
                  <SelectItem value="departamento_solicitante">Agrupar por departamento solicitante</SelectItem>
                </SelectContent>
              </Select>
              <button type="button" onClick={() => setShowProd((v) => !v)} data-testid="toggle-show-prod"
                className={`inline-flex items-center gap-1.5 rounded-sm h-9 px-3 text-sm font-medium border transition-colors ${showProd ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                title="Mostrar/ocultar desarrollos en producción">
                {showProd ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                Producción {!showProd && hiddenProdCount > 0 && <span className="font-mono text-xs">({hiddenProdCount})</span>}
              </button>
            </div>
          </div>

          <TabsContent value="table">
            {canWrite && selectedIds.size > 0 && (
              <div className="flex items-center gap-3 mb-3 bg-slate-900 text-white rounded-sm px-4 py-2.5" data-testid="bulk-action-bar">
                <span className="text-sm font-medium">{selectedIds.size} seleccionado(s)</span>
                <span className="text-slate-400 text-sm">Cambiar estatus a:</span>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger data-testid="bulk-status-select" className="rounded-sm w-48 h-8 bg-white text-slate-900 border-0"><SelectValue placeholder="Selecciona estatus" /></SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUSES[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={applyBulkStatus} disabled={applyingBulk || !bulkStatus} className="rounded-sm h-8 bg-blue-600 hover:bg-blue-700" data-testid="bulk-apply-btn">
                  {applyingBulk ? "Aplicando..." : "Aplicar"}
                </Button>
                <button onClick={clearSelection} className="ml-auto text-slate-300 hover:text-white text-sm" data-testid="bulk-clear-btn">Limpiar</button>
              </div>
            )}
            <GroupedDevTable devs={tableDevs} groupBy={groupBy} onRowClick={openEdit} onDelete={setToDelete} onNotify={handleNotify} isPM={isPM} canWrite={canWrite}
              selectable={canWrite} selectedIds={selectedIds} onToggleSelect={toggleSelect} onToggleSelectAll={toggleSelectAll} />
          </TabsContent>
          <TabsContent value="kanban">
            <KanbanBoard devs={filtered} onMove={handleMove} onCardClick={openEdit} canWrite={canWrite} />
          </TabsContent>
        </Tabs>
      </div>

      <DevDialog open={dialogOpen} onOpenChange={setDialogOpen} dev={editing} users={users} allDevs={devs} onSaved={load}
        catalog={catalog} onCatalogChange={setCatalog} departamentoOptions={departamentoOptions} solicitanteOptions={solicitanteOptions} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar desarrollo?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{toDelete?.title}</strong>. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
